import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { surfaceCard } from '../../../Shared/surfaceCard';
import BoardCell from './BoardCell';
import DueDateLane from './DueDateLane';

const NAME_COL = 140;

const WeekBoardShell = ({
	board,
	loading,
	isManager,
	showPerson = false,
	onCellCreate,
	onEditAssignment,
	onDueClick,
	emptyMessage = 'Nothing scheduled this week.',
}) => {
	const theme = useTheme();
	const columns = board?.columns || [];
	const rows = board?.rows || [];
	const dueByDate = board?.due_by_date || {};
	const colCount = columns.length || 1;

	if (loading && !board) {
		return (
			<Box
				sx={{
					...surfaceCard(theme),
					display: 'flex',
					justifyContent: 'center',
					py: 6,
				}}
			>
				<CircularProgress size={28} />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				...surfaceCard(theme),
				overflow: 'auto',
				WebkitOverflowScrolling: 'touch',
				maxWidth: '100%',
			}}
		>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: `${NAME_COL}px repeat(${colCount}, minmax(110px, 1fr))`,
					minWidth: NAME_COL + colCount * 110,
					position: 'relative',
				}}
			>
				{/* Header */}
				<Box
					sx={{
						position: 'sticky',
						left: 0,
						top: 0,
						zIndex: 4,
						backgroundColor: theme.palette.background.paper,
						borderBottom: '1px solid',
						borderRight: '1px solid',
						borderColor: 'divider',
						px: 1.5,
						py: 1.25,
					}}
				/>
				{columns.map((col) => (
					<Box
						key={`h-${col.date}`}
						sx={{
							position: 'sticky',
							top: 0,
							zIndex: 2,
							backgroundColor: theme.palette.background.paper,
							borderBottom: '1px solid',
							borderRight: '1px solid',
							borderColor: 'divider',
							px: 1,
							py: 1.25,
							textAlign: 'center',
						}}
					>
						<Typography variant="caption" fontWeight={700}>
							{col.label}
						</Typography>
					</Box>
				))}

				<DueDateLane
					columns={columns}
					dueByDate={dueByDate}
					onDueClick={onDueClick}
					stickyLeftWidth={NAME_COL}
				/>

				{rows.length === 0 ? (
					<Box
						sx={{
							gridColumn: `1 / -1`,
							px: 2,
							py: 4,
							textAlign: 'center',
						}}
					>
						<Typography color="text.secondary">{emptyMessage}</Typography>
					</Box>
				) : (
					rows.map((row) => (
						<React.Fragment key={row.id}>
							<Box
								sx={{
									position: 'sticky',
									left: 0,
									zIndex: 3,
									backgroundColor: theme.palette.background.paper,
									borderBottom: '1px solid',
									borderRight: '1px solid',
									borderColor: 'divider',
									px: 1.5,
									py: 1.25,
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									minWidth: NAME_COL,
									width: NAME_COL,
								}}
							>
								<Typography
									variant="body2"
									fontWeight={700}
									sx={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{row.label}
								</Typography>
								{row.customer_name ? (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
									>
										{row.customer_name}
									</Typography>
								) : null}
							</Box>
							{columns.map((col) => {
								const cell = row.cells?.[col.date] || { assignments: [] };
								return (
									<Box
										key={`${row.id}-${col.date}`}
										sx={{
											borderBottom: '1px solid',
											borderRight: '1px solid',
											borderColor: 'divider',
										}}
									>
										<BoardCell
											assignments={cell.assignments || []}
											canCreate={isManager}
											showPerson={showPerson}
											onCreate={() =>
												onCellCreate?.({
													row,
													date: col.date,
												})
											}
											onEdit={onEditAssignment}
										/>
									</Box>
								);
							})}
						</React.Fragment>
					))
				)}
			</Box>
		</Box>
	);
};

export const CrewWeekBoard = (props) => (
	<WeekBoardShell {...props} emptyMessage="No crew on the schedule yet." />
);

export const JobsWeekBoard = (props) => (
	<WeekBoardShell
		{...props}
		showPerson
		emptyMessage="No jobs due or assigned this week."
	/>
);

export default WeekBoardShell;
